import React from 'react';
import styles from './css/HeroButtons.styl';
import _ from 'lodash';
import {FormControls} from '../../../../shared/library';
import {bindMethods} from '../../../../shared/utilities/componentHelpers';
import {getGATags} from '../../../../shared/utilities/GAHelpers';
import {
  useButtonsWithoutDropdown,
} from '../../../../shared/utilities/personPageType';
import appConfig from '../../../../../appConfig/AppConfig';

const {Dropdown, Button} = FormControls;
const conf = appConfig();

/**
 * Person Hero buttons
 * @param   {Object}  props Passed in properties
 * @return  {JSX}     Returns the Buttons and Dropdown of the Person Hero
 */
class HeroButtons extends React.Component {
  static propTypes = {
    allowGuestBook: React.PropTypes.bool,
    allowGuestBookEntry: React.PropTypes.bool,
    allowPersonPagePosting: React.PropTypes.bool,
    fhShowFlowers: React.PropTypes.bool,
    flowerClickEvents: React.PropTypes.func,
    flowersLink: React.PropTypes.string,
    guestBookGALabel: React.PropTypes.string,
    guestBookLink: React.PropTypes.string,
    isSingleColumn: React.PropTypes.bool,
    layout: React.PropTypes.string,
    obituarySourceLink: React.PropTypes.string,
    pageTypeClass: React.PropTypes.string,
    personPageType: React.PropTypes.string,
    scrollToGuestbook: React.PropTypes.func,
    showFlowersAndGifts: React.PropTypes.bool,
    sourceId: React.PropTypes.number,
    totalCondolencesCount: React.PropTypes.number,
  };

  constructor(props) {
    super(props);

    this.state = {isDropdownToggled: false};

    bindMethods(this, [
      'flowersClickHandler',
      'getButtonItems',
      'getDropdownItems',
      'getMainButtonItem',
      'getPriorities',
      'getPrioritizedCtaItems',
      'readObituaryHandler',
      'setupButtons',
      'signGbClickHandler',
      'toggleDropdown',
    ]);
  }

  /**
   * Sets up the buttons for rendering
   * Has to be done during render and not in constructor only
   * because buttons can change from single to multi column views
   * @return {undefined}  returns nothing
   */
  setupButtons() {
    const ctaItems = [];

    const {
      allowGuestBookEntry,
      allowGuestBook,
      fhShowFlowers,
      flowersLink,
      flowerClickEvents,
      isSingleColumn,
      obituarySourceLink,
      personPageType,
      showFlowersAndGifts,
      sourceId,
      totalCondolencesCount,
    } = this.props;

    if (flowersLink && showFlowersAndGifts && fhShowFlowers) {
      ctaItems.push({
        ctaType: 'sendFlowers',
        displayText: 'Send Flowers',
        screenReader: 'Opens send flowers url in a new window',
        props: {
          className: styles.mainButton,
          handleClick: _.partial(
            this.flowersClickHandler,
            flowersLink,
            flowerClickEvents
          ),
          id: 'FlowersHeader',
        },
      });
    }

    if (!isSingleColumn && useButtonsWithoutDropdown(personPageType)) {
      ctaItems.push({
        ctaType: 'readObituary',
        displayText: 'Read Obituary',
        screenReader: 'Opens source obituary in a new window',
        props: {
          className: styles.mainButton,
          handleClick: _.partial(this.readObituaryHandler, obituarySourceLink),
          id: 'ReadObituaryHeader',
        },
      });
    } else if (allowGuestBookEntry) {
      ctaItems.push({
        ctaType: 'signGuestBook',
        displayText: 'Sign Guest Book',
        screenReader: 'Scrolls to Condolence Entry',
        props: {
          className: `${styles.mainButton} ${styles.signGBMainButton}`,
          handleClick: this.signGbClickHandler(),
          id: 'SignGuestBookEntryHeader',
        },
      });
    }

    const prioritizedCtaItems = this.getPrioritizedCtaItems(
      ctaItems,
      this.getPriorities()
    );

    if (useButtonsWithoutDropdown(personPageType)) {
      this.buttons = this.getButtonItems(prioritizedCtaItems);
      this.hasButtons = !_.isEmpty(this.buttons);
    } else {
      this.dropdownItems = this.getDropdownItems(prioritizedCtaItems);
      this.hasDropDown = !_.isEmpty(this.dropdownItems);

      this.mainButton = this.getMainButtonItem(prioritizedCtaItems);
      this.hasMainButton = !_.isEmpty(this.mainButton);
    }
  }

  /**
   * Determines the priorities of the CTA items
   * @method getPriorities
   * @return {array}  returns the ordered list of prioritized items
   */
  getPriorities() {
    return [
      'sendFlowers',
      'readObituary',
      'signGuestBook',
    ];
  }

  /**
   * Takes a list of cta items and injects the appropriate priority
   * NOTE: ctaTypes in the priorities need to match the ctaTypes in ctaItems
   * @method getPrioritizedCtaItems
   * @param  {array}  ctaItems    List of ctaItems without the priority property
   * @param  {array}  priorities  List of priorities
   * @return {array}  returns the list of ctaItems with the priority property
   */
  getPrioritizedCtaItems(ctaItems, priorities) {
    let priority = 1;

    return _.reduce(
      priorities,
      (result, ctaType) => {
        _.map(ctaItems, item => {
          if (ctaType === item.ctaType) {
            item.priority = priority;
            priority++;
            result.push(item);
          }
        });

        return result;
      },
      []
    );
  }

  /**
   * Gets the first priority ctaItem for the button display
   * @method getMainButtonItem
   * @param  {array}  prioritizedCtaItems The ctaItems with priority
   * @return {object} returns the button data object
   */
  getMainButtonItem(prioritizedCtaItems) {
    const button = _.find(prioritizedCtaItems, item => item.priority === 1);

    if (button) {
      button.props.btnType = 'none';
      button.props.type = 'button';
      button.displayText = (
        <span data-ga-no-auto-tag="true">
          {button.displayText}
          <span className={styles.srOnly}>{button.screenReader}</span>
        </span>
      );
    }

    return button;
  }

  /**
   * Gets the ctaItems for the buttons
   * @method getButtonItems
   * @param  {array}  prioritizedCtaItems The ctaItems with priority
   * @return {array} returns an array of the button data objects
   */
  getButtonItems(prioritizedCtaItems) {
    const buttons = prioritizedCtaItems.map(ctaItem => {
      ctaItem.props.btnType = 'none';
      ctaItem.props.type = 'button';
      ctaItem.props.className = `${styles.mainButton} ${
        ctaItem.ctaType === 'sendFlowers'
          ? styles.primaryButton
          : styles.secondaryButton
      }`;
      ctaItem.displayText = (
        <span data-ga-no-auto-tag="true">
          {ctaItem.displayText}
          <span className={styles.srOnly}>{ctaItem.screenReader}</span>
        </span>
      );
      return ctaItem;
    });

    return _.orderBy(buttons, ['priority']);
  }

  /**
   * Gets the ctaItems for the dropdown and orders them based on priority
   * @method getDropdownItems
   * @param  {array}  prioritizedCtaItems List of ctaItems with priority
   * @return {array}  returns the ordered array of dropdown diplay objects
   */
  getDropdownItems(prioritizedCtaItems) {
    const filteredCtaItems = _.reduce(
      prioritizedCtaItems,
      (result, item) => {
        if (item.priority > 1) {
          item.displayText = (
            <span data-ga-no-auto-tag="true">
              {item.displayText}
              <span className={styles.srOnly}>{item.screenReader}</span>
            </span>
          );

          item.props.onClick = item.props.handleClick;
          // eslint-disable-next-line
          delete item.props.handleClick;
          result.push(item);
        }
        return result;
      },
      []
    );

    return _.orderBy(filteredCtaItems, ['priority']);
  }

  /**
   * Fires the flowers GA event function, then opens the flowers link in a new window
   * @method flowersClickHandler
   * @param  {string}     flowersLink       Link for sympathy flowers
   * @param  {function}   flowerClickEvents GA events for flowers
   * @return {undefined}  returns nothing
   */
  flowersClickHandler(flowersLink, flowerClickEvents) {
    if (_.isFunction(flowerClickEvents)) {
      flowerClickEvents();
    }

    window.open(flowersLink, '_blank');
  }

  /**
   * Opens the obituary link in a new window
   * @method readObituaryHandler
   * @param  {string}     obituarySourceLink  Link for external obit
   * @return {undefined}  returns nothing
   */
  readObituaryHandler(obituarySourceLink) {
    window.open(obituarySourceLink, '_blank');
  }

  /**
   * Scrolls to the guest book and fires the GA events
   * @method signGbClickHandler
   * @return {undefined} returns nothing
   */
  signGbClickHandler() {
    return e => {
      e.preventDefault();
      this.props.scrollToGuestbook();
      getGATags({
        eventCategory: 'bypage_person',
        eventAction: 'sign_guest_book_header',
        eventLabel: 'onpage',
      });
    };
  }

  /**
   * Toggles whether the dropdown is open or closed
   * @return {undefined} returns nothing
   */
  toggleDropdown() {
    this.setState({isDropdownToggled: !this.state.isDropdownToggled});
  }

  render() {
    const {pageTypeClass} = this.props;

    this.setupButtons();
    return (
      <span className={styles[pageTypeClass] || styles.HeroButtons}>
        {this.hasButtons
          ? this.buttons.map(button => (
              <Button key={button.props.id} {...button.props}>
                {button.displayText}
              </Button>
            ))
          : null}
        {this.hasMainButton ? (
          <Button {...this.mainButton.props}>
            {this.mainButton.displayText}
          </Button>
        ) : null}
        {this.hasDropDown ? (
          <Dropdown
            btnClassName={styles.heroDropdownBtn}
            className={styles.heroDropdown}
            dropdownItems={this.dropdownItems}
            id="heroDropDown"
            isDropdownToggled={this.state.isDropdownToggled}
            tetherAttachment="top right"
            tetherOffset="-5px 0"
            tetherTargetAttachment="bottom right"
            toggleDropdown={this.toggleDropdown}
          />
        ) : null}
      </span>
    );
  }
}

export default HeroButtons;



// WEBPACK FOOTER //
// ./src/reactModules/pages/Person/components/PersonHero/HeroButtons.js